import React, { useState } from "react";
import { ChakraProvider, Text } from "@chakra-ui/react";
import { AlgoliaSearchProvider } from "components/algolia";
import { HubSpotFormModal } from "components/hubspot";
import { Page } from "components/page";
import PlaybooksCatalog from "components/playbooks/playbooks-catalog";
import { SimpleTextHero } from "components/hero";
import { identifyUser, trackEvent } from "lib/analytics";
import { getAnnouncementBanner, getPage, type Banner, type Page as SanityPage } from "lib/sanity";
import { type GetStaticProps, type NextPage } from "next";
import { renderToString } from "react-dom/server";
import { getServerState, type InstantSearchServerState } from "react-instantsearch";
import { theme } from "theme/theme";
import { getPageMetaImage } from "utils";

type PageProps = {
  page: SanityPage;
  serverState?: InstantSearchServerState;
  announcementBanner?: Banner;
};

const HUBSPOT_FIELDS = ["firstname", "lastname", "company"];
const HUBSPOT_FORM_ID = "bc652bd7-a30c-4927-98b9-cb52ce97bfc2";

const Playbooks: NextPage<Readonly<PageProps>> = ({ page, serverState, announcementBanner }) => {
  const [success, setSuccess] = useState(false);

  const onFormSubmit = (lead: Record<string, string>) => {
    trackEvent("Newsletter Signup", {
      Type: "Playbook of the Month",
      Email: lead.email,
      "First Name": lead.firstname,
      "Last Name": lead.lastname,
      page: window.location.href,
    });
    identifyUser({
      email: lead.email,
      firstName: lead.firstname,
      lastName: lead.lastName,
    });
    setSuccess(true);
  };

  return (
    <AlgoliaSearchProvider
      serverState={serverState}
      appId={process.env.NEXT_PUBLIC_ALGOLIA_PLAYBOOK_APP_ID ?? ""}
      searchApiKey={process.env.NEXT_PUBLIC_ALGOLIA_PLAYBOOK_SEARCH_API_KEY ?? ""}
    >
      <Page
        title={page.metaTitle}
        description={page.metaDescription}
        image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
        announcementBanner={announcementBanner}
      >
        <SimpleTextHero
          maxW="100%"
          chipText="Playbooks"
          title={page.title}
          description={page.description}
          actions={
            <HubSpotFormModal
              label="Subscribe to Playbook of the Month"
              displayedFields={HUBSPOT_FIELDS}
              formId={HUBSPOT_FORM_ID}
              modalHeading="Subscribe to Playbook of the Month"
              modalDescription="A monthly newsletter that features new step-by-step guides to power real use cases."
              modalLabel="Subscribe"
              successContent={success && <Text textStyle="caption1">Thank you for subscribing!</Text>}
              onSubmit={onFormSubmit}
            />
          }
          mb={10}
        />

        <PlaybooksCatalog />
      </Page>
    </AlgoliaSearchProvider>
  );
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner] = await Promise.all([getPage("/playbooks"), getAnnouncementBanner()]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const serverState = await getServerState(
    <ChakraProvider theme={theme}>
      <Playbooks page={page} announcementBanner={announcementBanner} />
    </ChakraProvider>,
    { renderToString },
  );

  return {
    props: {
      page,
      serverState,
      announcementBanner,
    },
    revalidate: 60,
  };
};

export default Playbooks;
